import React, { useMemo } from "react";
import Select from "react-select";
import { getCountryCallingCode } from "react-phone-number-input";
import type { Country } from "react-phone-number-input";

// Shape of the options react-phone-number-input passes to a custom
// `countrySelectComponent`. `divider` entries separate preferred countries.
interface RPNICountryOption {
  value?: string;
  label: string;
  divider?: boolean;
}

interface CountryOption {
  value: string;
  label: string;
}

interface CountrySelectProps {
  name?: string;
  value?: string;
  options: RPNICountryOption[];
  onChange: (value?: string) => void;
  disabled?: boolean;
  // Flag renderer supplied by react-phone-number-input (respects its flag config).
  iconComponent: React.ElementType;
}

/**
 * Searchable country dropdown for react-phone-number-input, built on the
 * react-select dependency already used across the app. Shows flag + country
 * name + calling code in the menu, and just the flag in the collapsed control.
 */
const CountrySelect: React.FC<CountrySelectProps> = ({
  name,
  value,
  options,
  onChange,
  disabled,
  iconComponent: Flag,
}) => {
  const countryOptions = useMemo<CountryOption[]>(
    () =>
      options
        .filter((o) => !o.divider && !!o.value)
        .map((o) => ({ value: o.value as string, label: o.label })),
    [options]
  );

  const selected = countryOptions.find((o) => o.value === value) || null;

  return (
    <Select
      inputId={name}
      classNamePrefix="countrySelect"
      isDisabled={disabled}
      isSearchable
      options={countryOptions}
      value={selected}
      onChange={(option) => onChange((option as CountryOption | null)?.value)}
      placeholder=""
      // formatOptionLabel renders both the dropdown rows and the collapsed
      // control; in the collapsed control (context "value") only the flag is
      // shown so the picker stays narrow next to the phone number field.
      formatOptionLabel={(option: CountryOption, { context }) =>
        context === "value" ? (
          <Flag country={option.value} label={option.label} />
        ) : (
          <span className="d-flex align-items-center w-100">
            <Flag country={option.value} label={option.label} />
            <span className="ms-2">{option.label}</span>
            <span className="ms-auto ps-3 text-muted">
              +{getCountryCallingCode(option.value as Country)}
            </span>
          </span>
        )
      }
      styles={{
        container: (base) => ({ ...base, minWidth: 96 }),
        // Strip the control's own chrome so it reads as one segment of the
        // phone field; a faint right divider separates it from the number.
        control: (base) => ({
          ...base,
          border: "none",
          borderRight: "1px solid #dee2e6",
          borderRadius: 0,
          boxShadow: "none",
          backgroundColor: "transparent",
          minHeight: 36,
        }),
        menu: (base) => ({ ...base, width: 320, zIndex: 5 }),
        indicatorSeparator: () => ({ display: "none" }),
        valueContainer: (base) => ({ ...base, paddingRight: 0 }),
      }}
    />
  );
};

export default CountrySelect;
